import { useTranslation } from "react-i18next"
import { CaretDown, Check } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { Chip } from "@/components/ui/chip"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import { Country } from "@/types/__generated__/union-types"

const ALL_AGENDA_TYPES = ["TIME_WINDOW", "TELECONSULTATION", "ALTERNATIVE_TRIAGE", "EXTERNAL_ASSISTANCE"] as const
type AGENDA_TYPE = (typeof ALL_AGENDA_TYPES)[number]

type TypesFilterProps = {
  value: AGENDA_TYPE[]
  countryCode: Country
  onChange: (value: AGENDA_TYPE[]) => void
}

const TypesFilter = ({ value, countryCode, onChange }: TypesFilterProps) => {
  const { t } = useTranslation()
  const count = value.length
  const agendaTypes = ALL_AGENDA_TYPES.filter((type) => type !== "TIME_WINDOW" || countryCode === "CH")

  const handleToggle = (item: AGENDA_TYPE, isSelected: boolean) => {
    const next = isSelected ? [...value, item] : value.filter((type) => type !== item)
    onChange(next)
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        asChild
        className="aria-checked:border-on-primary-surface aria-checked:bg-primary-surface aria-checked:text-on-primary-surface aria-checked:hover:enabled:bg-primary-surface-hovered aria-checked:focus:enabled:border-on-primary-surface outline-hidden">
        <Chip data-testid="types-filter-trigger" className="h-8 gap-0.5" checked={count > 0} type="button">
          <div className={cn("w-0 overflow-hidden transition-all duration-200", count > 0 && "w-5")}>
            <Check size={20} />
          </div>
          <span className="px-1.5">
            {t("patient.agenda.filters.typesFilter.types") + (count > 0 ? ` (${count})` : ``)}
          </span>
          <CaretDown size={20} />
        </Chip>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        data-testid="types-filter-content"
        className="absolute -top-1 grid gap-2 rounded-md border-none p-2 leading-5">
        <div className="grid gap-0.5">
          {agendaTypes.map((item) => {
            const isSelected = value.includes(item)
            return (
              <DropdownMenuCheckboxItem
                onSelect={(e) => e.preventDefault()}
                className="h-9"
                key={item}
                checked={isSelected}
                data-testid={`filter-item-${item.toLowerCase()}`}
                onCheckedChange={(checked) => handleToggle(item, checked)}>
                <div className="flex items-center">{t(`patient.agenda.filters.typesFilter.${item}`)}</div>
              </DropdownMenuCheckboxItem>
            )
          })}
        </div>
        <DropdownMenuSeparator className="bg-border -mx-2 my-0" />
        <div className="flex justify-end">
          <Button
            onClick={() => onChange([])}
            data-testid="reset-types-filter"
            variant="tertiary"
            className="h-9 rounded-full px-2 py-0 leading-5 font-medium">
            {t("common.button.reset")}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export { TypesFilter, type AGENDA_TYPE }
